/**
 * TasksScreenSkeleton Component
 *
 * Skeleton loading state for the tasks screen.
 * Shows placeholder for header and task list.
 */

import React from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

/**
 * TaskListSkeleton - list-only placeholder (6 task cards, no header/SafeArea).
 * Embeddable inside screens that render their own header, e.g. the To Do tab.
 */
export const TaskListSkeleton: React.FC = () => {
  const colorScheme = useColorScheme();
  const colors = Colors[colorScheme ?? 'light'];

  const skeletonStyle = {
    backgroundColor: colorScheme === 'dark' ? colors.border : colors.borderMuted,
  };

  return (
    <View style={styles.listContainer}>
      {Array.from({ length: 6 }).map((_, index) => (
        <View
          key={`task-${index}`}
          style={[
            styles.taskCard,
            {
              backgroundColor: colors.cardBackground,
              borderColor: colors.border,
            },
          ]}
        >
          <View style={styles.taskRow}>
            <View style={[styles.checkbox, skeletonStyle]} />
            <View style={styles.taskContent}>
              <View style={[styles.taskTitle, skeletonStyle, { width: index % 2 === 0 ? '80%' : '65%' }]} />
              <View style={[styles.taskMeta, skeletonStyle, { width: index % 3 === 0 ? '50%' : '40%' }]} />
            </View>
          </View>
        </View>
      ))}
    </View>
  );
};

/**
 * TasksScreenSkeleton - Loading placeholder for tasks screen
 *
 * Displays skeleton UI matching the tasks screen layout including
 * header and list of task items.
 */
export const TasksScreenSkeleton: React.FC = () => {
  const colorScheme = useColorScheme();
  const colors = Colors[colorScheme ?? 'light'];

  const skeletonStyle = {
    backgroundColor: colorScheme === 'dark' ? colors.border : colors.borderMuted,
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        {/* Header skeleton */}
        <View style={[styles.header, { borderBottomColor: colors.border }]}>
          <View style={styles.headerTop}>
            <View style={[styles.greeting, skeletonStyle]} />
            <View style={[styles.date, skeletonStyle]} />
          </View>
          <View style={styles.statsRow}>
            <View style={[styles.statCard, { backgroundColor: colors.cardBackground }]}>
              <View style={[styles.statNumber, skeletonStyle]} />
              <View style={[styles.statLabel, skeletonStyle]} />
            </View>
            <View style={[styles.statCard, { backgroundColor: colors.cardBackground }]}>
              <View style={[styles.statNumber, skeletonStyle]} />
              <View style={[styles.statLabel, skeletonStyle]} />
            </View>
          </View>
        </View>

        {/* Task list skeleton */}
        <TaskListSkeleton />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  header: {
    paddingHorizontal: Spacing.xl,
    paddingTop: Spacing.lg,
    paddingBottom: Spacing.xl,
    borderBottomWidth: 1,
    gap: Spacing.lg,
  },
  headerTop: {
    gap: Spacing.sm - 2, // 6px
  },
  greeting: {
    height: 28,
    width: 160,
    borderRadius: BorderRadius.xs,
  },
  date: {
    height: 18,
    width: 180,
    borderRadius: BorderRadius.xs,
  },
  statsRow: {
    flexDirection: 'row',
    gap: Spacing.md,
  },
  statCard: {
    flex: 1,
    padding: Spacing.lg,
    borderRadius: BorderRadius.md,
    gap: Spacing.sm,
    alignItems: 'center',
  },
  statNumber: {
    height: 32,
    width: 40,
    borderRadius: BorderRadius.xs,
  },
  statLabel: {
    height: 14,
    width: 80,
    borderRadius: BorderRadius.xs,
  },
  listContainer: {
    flex: 1,
    padding: Spacing.lg,
    gap: Spacing.md,
  },
  taskCard: {
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    padding: Spacing.lg,
  },
  taskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12, // circular
  },
  taskContent: {
    flex: 1,
    gap: Spacing.sm,
  },
  taskTitle: {
    height: 18,
    borderRadius: BorderRadius.xs,
  },
  taskMeta: {
    height: 14,
    borderRadius: BorderRadius.xs,
  },
});
